import { toast } from '@backyard-ui/core';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { HttpClient } from '@/application/interfaces/http-client';
import { ErrorReporter } from '@/infra/monitoring/error-reporter';
import { Services } from '@/infra/services';
import {
  type Response as AddProductsToCartResponse,
  type ErrorResponse,
  type Data,
} from '@/infra/services/checkout/cart/add-products-to-cart';
import { useUserResources } from '@/presenters/hooks/user';
import { useGlobalStore } from '@/presenters/store/global';
import {
  API_TOKEN,
  CART_ID,
  JWT_BEFORE_SESSION,
} from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';

import { DEFAULT_ERROR_MESSAGE } from './constants';

type ErrorType = HttpClient.Response<ErrorResponse> | Error;

export interface UseAddProductsToCartProps
  extends UseMutationOptions<AddProductsToCartResponse, ErrorType, Data> {
  postSuccess?: (
    data: AddProductsToCartResponse,
    variable: Data
  ) => Promise<void>;
}

const useAddProductsToCart = (options: Readonly<UseAddProductsToCartProps>) => {
  const { onError, onSuccess, postSuccess } = options;

  const { updateCartItems } = useUserResources();

  const region = useGlobalStore((state) => state.session?.region);
  const cartId = useGlobalStore((state) => state.session?.cartId);

  const result = useMutation({
    mutationFn: async (variable) => {
      const data = await Services.Checkout.Cart.addProductsToCart(variable);

      await postSuccess?.(data, variable);

      return data;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      const cartIdCookieOptions = cookies.createOptions({
        name: CART_ID,
        value: data.data.id,
      });

      cookies.client.set(cartIdCookieOptions);

      updateCartItems(data.data.uniqueProductsCount);
      onSuccess?.(data, variables, context);
    },
    onError: async (error, variable, context) => {
      const isKnownError =
        error instanceof HttpClient.Response && error.body?.errors?.length;

      const errorMessage =
        isKnownError && error.body.errors?.[0].message
          ? error.body.errors[0].message
          : DEFAULT_ERROR_MESSAGE;

      toast({
        description: errorMessage,
        appearance: 'critical',
      });

      if (!isKnownError) {
        ErrorReporter.report(new Error('AddProductToCartException'), {
          errorMessage,
          productId: variable.products[0]?.id,
          region,
          cartId,
        });
      }

      onError?.(error, variable, context);
    },
    retry: (failureCount, error) => {
      if (
        failureCount === 0 &&
        error instanceof HttpClient.Response &&
        error.status === 401
      ) {
        cookies.client.remove(API_TOKEN);
        cookies.client.remove(JWT_BEFORE_SESSION);

        return true;
      }

      return false;
    },
  });

  return result;
};

export default useAddProductsToCart;
